import { useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { memberApi, useMemberMutation, errorMessage, useMyStories } from "@/hooks/useMember";

const inputCls = "w-full bg-cream border border-forest/20 px-4 py-3 text-sm text-charcoal focus:border-forest outline-none transition-colors";
const statusLabel = { pending: "Awaiting moderation", published: "Published", rejected: "Not published", withdrawn: "Withdrawn" };

export default function StoryShare({ trials }) {
  const { user } = useAuth();
  const { data: stories = [] } = useMyStories();
  const eligible = trials.filter((t) => ["in_progress", "completed"].includes(t.status));
  const [body, setBody] = useState("");
  const [displayName, setDisplayName] = useState(user?.display_name || user?.first_name || "");
  const [trialId, setTrialId] = useState(eligible[0]?.trial_id || "");
  const [consent, setConsent] = useState(false);
  const [preview, setPreview] = useState(false);
  const submit = useMemberMutation(memberApi.submitStory, () => { toast.success("Thank you. Your story has been sent for review."); setBody(""); setConsent(false); setPreview(false); });
  const withdraw = useMemberMutation(memberApi.withdrawStory, () => toast("Story withdrawn."));

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="story-share">
      <div className="lg:col-span-7 border border-forest/15 bg-cream p-6 sm:p-8">
        <p className="eyebrow">Voluntary</p>
        <h3 className="mt-4 font-serif font-light text-3xl text-forest">Share a public story</h3>
        <p className="mt-3 text-sm text-charcoal/65 font-light">Separate from your private check-ins. Positive or critical, in your own words. Stories are reviewed before publishing and shown exactly as previewed. You can withdraw at any time.</p>
        {eligible.length === 0 ? (
          <p className="mt-6 text-sm text-charcoal/55 font-light border-t border-forest/10 pt-6">Public stories open once you have an in-progress or completed trial.</p>
        ) : !preview ? (
          <div className="mt-6 space-y-5">
            {eligible.length > 1 && (
              <select value={trialId} onChange={(e) => setTrialId(e.target.value)} className={inputCls}>{eligible.map((t) => <option key={t.trial_id} value={t.trial_id}>{t.programme?.title}</option>)}</select>
            )}
            <label className="block"><span className="text-sm text-charcoal/80">Display name (shown publicly)</span>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={60} className={`mt-2 ${inputCls}`} data-testid="story-display-name" /></label>
            <label className="block"><span className="text-sm text-charcoal/80">Your story</span>
              <textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} maxLength={1200} className={`mt-2 ${inputCls}`} data-testid="story-body" placeholder="How did this ritual fit into your everyday life?" />
              <span className="block mt-1 text-right text-xs text-charcoal/40">{body.length}/1200</span></label>
            <button type="button" onClick={() => { if (body.trim().length < 20) { toast.error("Please write at least a couple of sentences."); return; } setPreview(true); }} className="btn btn-outline" data-testid="story-preview-button">Preview exactly as it will appear</button>
          </div>
        ) : (
          <div className="mt-6" data-testid="story-preview">
            <p className="text-[10px] uppercase tracking-[0.22em] text-champagne">Exact preview</p>
            <blockquote className="mt-4 border border-forest/15 bg-ivory p-6">
              <p className="font-serif text-xl text-charcoal/85 leading-relaxed">"{body.trim()}"</p>
              <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-moss">{displayName.trim() || "Member"}</p>
            </blockquote>
            <label className="mt-6 flex items-start gap-3 text-sm text-charcoal/80 cursor-pointer">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="story-consent" />
              I consent to ELA Rituals publishing this story and display name on the Rituals With You website, exactly as shown above, until I withdraw it.
            </label>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button type="button" disabled={!consent || submit.isPending} onClick={() => submit.mutate({ trial_id: trialId || null, body, display_name: displayName.trim() || "Member", consent_publish: consent }, { onError: (e) => toast.error(errorMessage(e)) })} className="btn btn-primary disabled:opacity-50" data-testid="story-submit">Submit for review</button>
              <button type="button" onClick={() => setPreview(false)} className="btn btn-outline">Edit</button>
            </div>
          </div>
        )}
      </div>
      <div className="lg:col-span-5">
        <p className="text-[10px] uppercase tracking-[0.22em] text-champagne">Your stories</p>
        {stories.length === 0 ? <p className="mt-3 text-sm text-charcoal/55 font-light">Nothing shared yet.</p> : (
          <ul className="mt-4 space-y-3">
            {stories.map((s) => (
              <li key={s.story_id} className="border border-forest/15 bg-cream p-5" data-testid={`my-story-${s.story_id}`}>
                <div className="flex justify-between gap-3 text-[10px] uppercase tracking-[0.2em]"><span className="text-moss">{statusLabel[s.status] || s.status}</span><span className="text-charcoal/40">{new Date(s.created_at).toLocaleDateString("en-GB")}</span></div>
                <p className="mt-3 text-sm text-charcoal/75 font-light line-clamp-3">"{s.body}"</p>
                {["pending", "published"].includes(s.status) && (
                  <button onClick={() => withdraw.mutate(s.story_id)} className="mt-3 text-[11px] uppercase tracking-[0.2em] text-charcoal/50 hover:text-destructive link-underline" data-testid="story-withdraw">Withdraw</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
